export const ONBOARDING_PAYWALL_COPY_KEY = "ob_paywall_copy";
export const ONBOARDING_PAYWALL_COPY_TIMEOUT_MS = 3000;

export interface OnboardingPaywallCopy {
  titleLine1: string;
  titleLine2: string;
  body: string;
  ticker: string[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const isText = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

export function parseOnboardingPaywallCopy(
  raw: string,
  locale: string,
  fallback: OnboardingPaywallCopy,
): OnboardingPaywallCopy {
  try {
    const config: unknown = JSON.parse(raw);
    if (!isRecord(config)) {
      return fallback;
    }
    const copy = config[locale.toLowerCase()];
    if (!isRecord(copy)) {
      return fallback;
    }
    return {
      titleLine1: isText(copy.titleLine1)
        ? copy.titleLine1
        : fallback.titleLine1,
      titleLine2: isText(copy.titleLine2)
        ? copy.titleLine2
        : fallback.titleLine2,
      body: isText(copy.body) ? copy.body : fallback.body,
      ticker:
        Array.isArray(copy.ticker) && copy.ticker.every(isText)
          ? copy.ticker
          : fallback.ticker,
    };
  } catch {
    return fallback;
  }
}

export async function loadOnboardingPaywallCopy(
  read: () => Promise<string>,
  locale: string,
  fallback: OnboardingPaywallCopy,
): Promise<OnboardingPaywallCopy> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      Promise.resolve()
        .then(read)
        .then((raw) => parseOnboardingPaywallCopy(raw, locale, fallback)),
      new Promise<OnboardingPaywallCopy>((resolve) => {
        timer = setTimeout(
          () => resolve(fallback),
          ONBOARDING_PAYWALL_COPY_TIMEOUT_MS,
        );
      }),
    ]);
  } catch {
    return fallback;
  } finally {
    clearTimeout(timer);
  }
}
